import { ldb, state } from '@codesuma/baseline'
import { CATALOG_ENDPOINT } from '../configs/app'
import { BUNDLED_INDEX } from '../configs/catalog'
import {
    ICatalogCountry,
    ICatalogIndex,
    remoteCountryVersion,
    shouldReplace,
    validateCountry,
    validateIndex,
} from './catalog-payload'

// Delivers country holiday corrections without an app release, the same way
// services/hijri-adjustments.ts delivers moon-sighting ones.
//
// Priority, highest first:
//   1. downloaded — the newest payload fetched from CATALOG_ENDPOINT
//   2. stored     — that payload, persisted, so it still works offline
//   3. bundled    — configs/catalog.ts, compiled into bundle.js at build time
//
// 1 and 2 are the same thing: a fetch only takes effect by being stored. The
// bundled copy is the floor, and it costs neither a network nor a wait — a first
// run has every country the build shipped with before it draws anything. A
// version decides between them: the app takes a downloaded country only when its
// version is higher than the one it holds, so a stale mirror can never roll a
// correction back.
//
// What travels is rules, never dates — one small file per country, good for any
// year. The resolver in occasion-catalog.ts turns them into days on the device.
//
// The index is the cheap version check. A country's own file is fetched the
// first time it is needed, stored with its version, and only fetched again when
// the index says that country has moved. An unchanged Netherlands is not
// re-downloaded because the United States gained a state.

const KEY = (name: string) => `catalog:${name}`
const CHECKED = 'catalog:checked'
const CHECK_INTERVAL = 24 * 60 * 60 * 1000

// The copy that travels with the build. Relative on purpose: on the web it is
// the same deploy the endpoint points at, and on native it is the app's own
// files, so a country costs no cross-origin request and works with no network.
// Out of bundle.js, not off the device.
const SEED = '/data/occasions'

// The winner for each name, so the day slide reads it without touching storage.
const memory = new Map<string, ICatalogIndex | ICatalogCountry>()
// The load already running for a name, so a page asking twice in one breath
// shares one request. It holds the promise and not a flag: a second caller needs
// the payload, and handing it null would draw an empty country over a good one.
const inFlight = new Map<string, Promise<ICatalogIndex | ICatalogCountry | null>>()

type Check<T> = (payload: unknown) => T | null

// Only the directory ships inside the app. A country's occasions are fetched the
// first time somebody opens it, and stored from then on — see the note in
// scripts/generate-occasions.ts for why they are not bundled.
const bundledFor = (name: string): ICatalogIndex | ICatalogCountry | null =>
    name === 'index' ? BUNDLED_INDEX : null

const lastChecked = (): Record<string, number> => ldb.get(CHECKED) || {}
const markChecked = (name: string) => ldb.set(CHECKED, { ...lastChecked(), [name]: Date.now() })

// Something newer reached memory than what the page last drew with.
let stamp = 0
const arrived = () => state.set('country-occasions', `catalog-${++stamp}`)

// The best copy already on the device: the stored download when it is newer than
// the build, the compiled-in one otherwise. Synchronous on purpose — the grid
// and the day slide draw before any promise could settle.
function best<T extends ICatalogIndex | ICatalogCountry>(
    name: string, check: Check<T>,
): T | null {
    const held = memory.get(name) as T | undefined
    if (held) return held

    const stored = check(ldb.get(KEY(name)))
    const base = bundledFor(name) as T | null
    const winner = stored && (!base || stored.version > base.version) ? stored : base
    if (winner) memory.set(name, winner)
    return winner
}

async function fetchJson(url: string, opts?: RequestInit): Promise<unknown> {
    try {
        const response = await fetch(url, opts)
        if (!response.ok) return null
        return await response.json() as unknown
    } catch {
        // Offline, DNS failure, malformed JSON. Whatever is already held stands,
        // and the next launch tries again.
        return null
    }
}

const liveUrl = (name: string) => `${CATALOG_ENDPOINT}/${name}.json`

function keep<T extends ICatalogIndex | ICatalogCountry>(name: string, payload: T): T {
    memory.set(name, payload)
    ldb.set(KEY(name), payload)
    arrived()
    return payload
}

// One conditional GET of the directory. `cache: 'no-cache'` makes the platform
// revalidate using its own ETag, so an unchanged file costs a 304 with no body.
// Raw fetch rather than baseline's http because that one sends credentials,
// which a static host answering `Access-Control-Allow-Origin: *` cannot accept
// — the same reason hijri-adjustments.ts reaches for fetch.
async function refreshIndex(current: ICatalogIndex) {
    if (!CATALOG_ENDPOINT) return
    if (navigator.onLine === false) return
    if (Date.now() - (lastChecked()['index'] || 0) < CHECK_INTERVAL) return

    const payload = validateIndex(await fetchJson(liveUrl('index'), { cache: 'no-cache' }))
    markChecked('index')
    if (!payload || payload.version <= current.version) return
    keep('index', payload)
    for (const country of payload.countries) {
        const held = memory.get(country.code) as ICatalogCountry | undefined
        if (held) void pullCountryIfNewer(country.code, held, payload)
    }
}

// A country's file is large and grows with every state. It is only fetched when
// the index — already refreshed on its own interval — says this country moved.
async function pullCountryIfNewer(
    code: string,
    current: ICatalogCountry,
    index?: ICatalogIndex,
) {
    const remote = remoteCountryVersion(index ?? heldIndex() ?? null, code)
    if (!shouldReplace(current.version, remote)) return
    if (!CATALOG_ENDPOINT || navigator.onLine === false) return
    const payload = validateCountry(await fetchJson(liveUrl(code), { cache: 'no-cache' }))
    if (!payload || !shouldReplace(current.version, payload.version)) return
    keep(code, payload)
}

function ensure<T extends ICatalogIndex | ICatalogCountry>(
    name: string, check: Check<T>,
): Promise<T | null> {
    const here = best(name, check)
    if (here) {
        if (name === 'index') void refreshIndex(here as ICatalogIndex)
        else void pullCountryIfNewer(name, here as ICatalogCountry)
        return Promise.resolve(here)
    }
    // Nothing bundled and nothing stored: a country published after this build
    // shipped. That is the only case that has to wait for the network.
    const running = inFlight.get(name) as Promise<T | null> | undefined
    if (running) return running

    const started = (async () => {
        // The same deploy on the web and the app's own files on native, so this
        // costs no cross-origin request and works with no network once
        // installed. It is out of bundle.js, not off the device.
        const seeded = check(await fetchJson(`${SEED}/${name}.json`))
        if (seeded) {
            if (name === 'index') void refreshIndex(seeded as ICatalogIndex)
            else void pullCountryIfNewer(name, seeded as ICatalogCountry)
            return keep(name, seeded)
        }
        // Only a country published after this build gets this far.
        if (!CATALOG_ENDPOINT) return null
        const live = check(await fetchJson(liveUrl(name), { cache: 'no-cache' }))
        return live ? keep(name, live) : null
    })().finally(() => inFlight.delete(name))

    inFlight.set(name, started)
    return started
}

const heldIndex = () => best('index', validateIndex) ?? undefined
const heldCountry = (code: string) => best(code, validateCountry) ?? undefined

const index = () => ensure('index', validateIndex)
const country = (code: string) => ensure(code, validateCountry)

// Looks for anything newer than the build in the background. Nothing has to be
// primed first: the compiled-in copy is already there.
function init(codes: string[]) {
    void index()
    for (const code of codes) void country(code)
}

export default { init, index, country, heldIndex, heldCountry }
